import { useEffect, useState } from 'react';
import { UseFormRegisterReturn, FieldError } from 'react-hook-form';
import { Check } from 'lucide-react';

interface PapTemplateSelectProps {
  formBinding: UseFormRegisterReturn;
  options: any[];
  setValue: (name: string, value: any) => void;
  value?: string;
  fieldName: string;
  required?: boolean;
  fieldLabel: string;
  error?: FieldError;
}

const PapTemplateSelect: React.FC<PapTemplateSelectProps> = ({ formBinding, options, setValue, value, fieldName, required = false, fieldLabel, error }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const selectTemplate = (templateId: string) => {
    return () => {
      updateValue(templateId);
    };
  };

  useEffect(() => {
    if (value) {
      updateValue(value);
    }
  }, [value]);

  const updateValue = (newValue: string) => {
    setSelectedTemplate(newValue);
    setValue(fieldName, newValue);
  };

  const isSelected = (templateId: string) => {
    return selectedTemplate === templateId;
  };

  return (
    <div className="mb-5">
      <label className="form-label">
        {fieldLabel}{required && <span className="text-destructive ml-1">*</span>}
      </label>
      <input {...formBinding}
        type="hidden"
        name={fieldName}
        id={fieldName}
        value={selectedTemplate}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {options.map((option: any) => (
          <div
            key={option.id}
            className={`
              relative cursor-pointer rounded-xl border-2 p-2 transition-all duration-200
              group/template overflow-hidden
              ${isSelected(option.id)
                ? 'border-accent bg-accent/5 dark:bg-accent/10 shadow-soft-sm ring-1 ring-accent/20 -translate-y-0.5'
                : 'border-border/40 hover:border-muted-foreground/30 hover:shadow-soft-sm hover:bg-muted/30 hover:-translate-y-0.5'
              }
            `}
            onClick={selectTemplate(option.id)}
          >
            {/* Selected check badge */}
            {isSelected(option.id) && (
              <div className="absolute top-3 right-3 z-10 w-6 h-6 rounded-full bg-accent flex items-center justify-center shadow-soft-sm">
                <Check className="w-3.5 h-3.5 text-accent-foreground" />
              </div>
            )}

            {/* Template image */}
            <div className="relative rounded-lg overflow-hidden">
              <img
                src={option.thumb_url || option.image}
                alt={option.name}
                className={`
                  w-full rounded-lg transition-transform duration-300
                  ${isSelected(option.id) ? '' : 'group-hover/template:scale-[1.02]'}
                `}
              />
            </div>

            {/* Template name */}
            <p className={`
              text-center text-sm mt-2 font-medium transition-colors duration-200
              ${isSelected(option.id) ? 'text-accent' : 'text-muted-foreground group-hover/template:text-foreground'}
            `}>
              {option.name}
            </p>
          </div>
        ))}
      </div>
      {error && <p className="mt-2 text-sm text-destructive">{error.message}</p>}
    </div>
  );
};

export default PapTemplateSelect;
